// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * PDF text extraction.
 *
 * TWO CHOICES HERE LOOK WRONG AND ARE BOTH FORCED BY MEASUREMENT.
 *
 * 1. THE LEGACY BUILD, ALWAYS. pdfjs-dist 6.2.108's modern build evaluates
 *    `typeof Iterator.prototype.join` at module top level in BOTH pdf.mjs and
 *    pdf.worker.mjs, and additionally uses Map.prototype.getOrInsertComputed
 *    (16 unguarded uses in the worker) and Uint8Array.prototype.toHex, which is
 *    reached on EVERY document open through the fingerprints getter. On any
 *    engine missing those, the result is a hard ReferenceError: blank page, zero
 *    text, no useful error. Legacy polyfills all of it through core-js and costs
 *    +35,719 bytes gzipped. That is the price of the tool working on a
 *    two-year-old iPad, and it is not negotiable — if someone "optimises" this
 *    import later, the tool silently stops extracting anything.
 *
 * 2. THE MAIN THREAD, NOT A WORKER. pdf.js already parses inside its own worker,
 *    so putting it in one of ours nests two workers and costs 2.3-3.4x: measured
 *    525 ms against 1215 ms for a 15-page paper, and 439 ms against 1489 ms for
 *    67 pages. `streamTextContent()` gives nothing back (556.6 vs 525.5 ms).
 *    Everything else in this tool — the index, the aligner, DOCX, EPUB — does
 *    run in our worker.
 */

import type { ExtractedSource, SourcePage } from '../types.ts';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import workerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import { candidatesForPage, formatFolio, harvest, labelsUsable } from './folio.ts';
import type { FolioCandidate } from './folio.ts';
import { joinPages, pageLines, stripRunningLines } from './layout.ts';
import type { Line, PositionedItem } from './layout.ts';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * A page yielding fewer characters than this is treated as carrying no text.
 * Full-bleed image pages and blank versos both land here.
 */
const MIN_PAGE_CHARS = 100;

/**
 * A page holding less than this share of the document's typical page is ALSO
 * treated as carrying no text, however far above the absolute floor it sits.
 *
 * WHY A RELATIVE FLOOR IS NEEDED AS WELL AS AN ABSOLUTE ONE. A scanned sheet is
 * rarely blank: it carries a Bates number, a library stamp, an "EXHIBIT A"
 * label, or a repository footer. Measured on a six-page mixed PDF — three
 * born-digital sheets and three scans each stamped with a 123-character JSTOR
 * footer — the absolute floor of 100 does not fire on 123, so the scans were
 * recorded as ordinary text pages, no warning was shown anywhere, and a
 * quotation physically printed on sheet 4 came back NOT FOUND with no hedge.
 * That is the accusation this tool exists not to make. 123 against a document
 * whose real pages hold ~1,880 characters is 6.5% of typical, so the relative
 * floor catches it while leaving a genuinely sparse part-opener or a short last
 * page of a chapter alone.
 */
const LOW_PAGE_SHARE = 0.2;

/** The count below which a sheet is not credible as a searched page. */
function lowTextFloor(counts: number[]): number {
  if (counts.length === 0) return MIN_PAGE_CHARS;
  const sorted = [...counts].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  return Math.max(MIN_PAGE_CHARS, Math.round(median * LOW_PAGE_SHARE));
}

/** A source whose median page is below the page floor cannot be searched at all. */
function decideUnsearchable(pages: SourcePage[]): boolean {
  if (pages.length === 0) return true;
  const counts = pages.map((p) => p.chars).sort((a, b) => a - b);
  const median = counts[Math.floor(counts.length / 2)];
  return median < MIN_PAGE_CHARS;
}

export interface PdfProgress {
  (done: number, total: number): void;
}

export async function extractPdf(
  id: number,
  name: string,
  data: ArrayBuffer,
  onProgress?: PdfProgress,
): Promise<ExtractedSource> {
  // getDocument({data}) DETACHES the buffer it is handed — byteLength becomes 0
  // — so it gets a copy and the caller keeps a usable original.
  const copy = new Uint8Array(data.slice(0));

  const task = pdfjs.getDocument({
    data: copy,
    // Character maps for predefined CID fonts. Without them a CJK PDF returns
    // `items: []` with nothing but a console warning, and every quotation
    // checked against it would come back NOT FOUND.
    cMapUrl: `${import.meta.env.BASE_URL}cmaps/`,
    cMapPacked: true,
    // The standard-14 font programs, for PDFs that name Helvetica or Times
    // without embedding them.
    //
    // THIS WAS ORIGINALLY OMITTED ON PURPOSE and the omission was wrong. The
    // reasoning was that nothing is rendered to a canvas, and that extracted
    // strings measured byte-identical with and without the font data — which is
    // still true on the bundled sample. What it missed is that leaving it out
    // makes pdf.js log "UnknownErrorException: Ensure that the
    // `standardFontDataUrl` API parameter is provided" once per non-embedded
    // font — measured at six over the bundled sample, and zero with this set —
    // and each of those is a font that FAILED to load. This tool derives its word separators from
    // `item.width` (see layout.ts), and that width comes out of the font
    // program, so a failed font load is a geometry failure waiting to happen,
    // not a cosmetic warning. 780 KB, served like the cmaps and, like them,
    // deliberately kept out of the service-worker precache (see vite.config.ts).
    standardFontDataUrl: `${import.meta.env.BASE_URL}standard_fonts/`,
    // Still true: no canvas, so no @font-face need ever be installed. This
    // switches off building browser font faces, NOT loading the font data.
    disableFontFace: true,
  });

  const doc = await task.promise;
  try {
    return await readDocument(id, name, data.byteLength, doc, onProgress);
  } finally {
    // PDFDocumentProxy has no destroy() in v6; the loading task owns teardown.
    await task.destroy();
  }
}

/**
 * Everything that happens to an already-opened document.
 *
 * Split out from `extractPdf` so the test suite can drive it against real PDFs
 * in Node without the browser-only worker-URL import — which means the layout,
 * folio and page-numbering logic is tested against actual files from actual
 * producers rather than only against synthetic geometry.
 */
export async function readDocument(
  id: number,
  name: string,
  bytes: number,
  doc: pdfjs.PDFDocumentProxy,
  onProgress?: PdfProgress,
): Promise<ExtractedSource> {
  const total = doc.numPages;
  const rawPages: Line[][] = [];
  const bandCandidates: FolioCandidate[][] = [];

  {
    for (let n = 1; n <= total; n++) {
      const page = await doc.getPage(n);
      const viewport = page.getViewport({ scale: 1 });
      const height = viewport.height;
      const width = viewport.width;
      const content = await page.getTextContent();

      const items: PositionedItem[] = [];
      for (const raw of content.items) {
        const item = raw as { str?: string; transform?: number[]; width?: number; height?: number };
        if (typeof item.str !== 'string' || !item.transform) continue;
        const m = pdfjs.Util.transform(viewport.transform, item.transform);
        items.push({
          str: item.str,
          x: m[4],
          y: m[5],
          width: item.width ?? 0,
          height: item.height ?? 0,
        });
      }

      // The folio is harvested from the RAW items, before running heads are
      // stripped — the folio usually IS part of the running head.
      bandCandidates.push(candidatesForPage(items, height, width));
      rawPages.push(pageLines(items, width));

      // Without this, memory grows across a long book: the page proxy holds its
      // operator list and font cache until told to let go.
      page.cleanup();
      onProgress?.(n, total);
    }
  }

  const stripped = stripRunningLines(rawPages);
  const { text, pageStarts } = joinPages(stripped.pages);

  // COUNTED OFF THE ASSEMBLED TEXT, NOT OFF THE RAW ITEMS. The earlier version
  // summed `item.str.length` over the pdf.js items, which counts the zero-height
  // spacers dropPhantoms throws away — on a real arXiv paper 34% of items — and
  // so overstates what was actually searched. It also counted running heads that
  // are about to be stripped out: a wholly scanned book whose only text was one
  // repeating 123-character stamp scored 123 characters a page, was declared
  // searchable, and then had every one of those stamps removed as furniture,
  // leaving a "searchable" source containing literally zero characters and
  // returning NOT FOUND for every quotation in it. This number is now the number
  // of characters a quotation could actually be found in.
  const charCounts = stripped.pages.map((lines) => lines.reduce((n, line) => n + line.text.length, 0));

  // Only pages that yielded (almost) no text are probed for an image. The probe
  // is getOperatorList, which is heavy on a text page — but a page below the
  // floor has a nearly empty content stream by construction, so this never runs
  // over a page of prose. The gate used to be `chars === 0`, which meant a
  // single stamped character on a scan was enough to record the sheet as fully
  // searched.
  const floor = lowTextFloor(charCounts);
  const imageOnly: boolean[] = new Array<boolean>(total).fill(false);
  for (let n = 1; n <= total; n++) {
    if ((charCounts[n - 1] ?? 0) >= floor) continue;
    const page = await doc.getPage(n);
    imageOnly[n - 1] = await pageHasImage(page);
    page.cleanup();
  }

  const labels = await safeLabels(doc);
  const believable = labelsUsable(labels);
  const harvested = harvest(bandCandidates);

  // POLICY: harvested folio first, degeneracy-filtered labels as a genuine
  // fallback, and abstention where the two disagree.
  //
  // Measured over 3,429 pages of 24 documents: the two fired together on 2,710
  // pages and disagreed on none of them, and the labels rescued 276 pages the
  // harvester abstained on — the first page of a journal article, where the
  // masthead folio sits at its own height, and the section boundaries of a
  // scanned book where the offset changes. Refusing the labels would cost every
  // one of those for a conflict that has not yet been observed to happen.
  let corroborated = 0;
  let conflicts = 0;
  let labelOnly = 0;
  const pages: SourcePage[] = [];
  for (let n = 1; n <= total; n++) {
    const harvestedFolio = harvested.assign.get(n);
    const folio = harvestedFolio ? formatFolio(harvestedFolio) : null;
    const labelled = believable ? (labels?.[n - 1] ?? '').trim() || null : null;

    let printed: string | null = null;
    let from: SourcePage['from'] = 'none';
    if (folio && labelled) {
      if (folio === labelled) {
        printed = folio;
        from = 'both';
        corroborated++;
      } else {
        // Both spoke and they contradict each other. Report the folio, because
        // it is what is physically printed on the page, but mark the page so
        // no pinpoint is ever called wrong against it.
        printed = folio;
        from = 'conflict';
        conflicts++;
      }
    } else if (folio) {
      printed = folio;
      from = 'folio';
    } else if (labelled) {
      printed = labelled;
      from = 'label';
      labelOnly++;
    }

    pages.push({
      sheet: n,
      start: pageStarts[n - 1] ?? 0,
      printed,
      from,
      folio,
      labelled,
      chars: charCounts[n - 1] ?? 0,
      imageOnly: imageOnly[n - 1] ?? false,
    });
  }

  const covered = pages.filter((p) => p.printed !== null).length;
  let provenance: ExtractedSource['provenance'] = 'sheet';
  if (covered > 0) {
    if (conflicts > corroborated) provenance = 'folio-contested';
    else if (corroborated > 0) provenance = 'folio-corroborated';
    else if (labelOnly > covered / 2) provenance = 'labels';
    else provenance = 'folio';
  }

  return {
    id,
    name,
    kind: 'pdf',
    bytes,
    text,
    pages,
    provenance,
    unsearchable: decideUnsearchable(pages),
    runningLinesDropped: stripped.dropped,
    pageNote: describePages(provenance, covered, total, harvested.runs, conflicts),
  };
}

async function safeLabels(doc: pdfjs.PDFDocumentProxy): Promise<string[] | null> {
  try {
    return await doc.getPageLabels();
  } catch {
    return null;
  }
}

/** Does a page with (almost) no text actually paint an image? */
async function pageHasImage(page: pdfjs.PDFPageProxy): Promise<boolean> {
  try {
    // getOperatorList is heavy, which is why it only ever runs on a page that
    // already fell below the low-text floor.
    const ops = await page.getOperatorList();
    const paint = new Set<number>([
      pdfjs.OPS.paintImageXObject,
      pdfjs.OPS.paintImageXObjectRepeat,
      pdfjs.OPS.paintInlineImageXObject,
      pdfjs.OPS.paintImageMaskXObject,
      pdfjs.OPS.paintImageMaskXObjectRepeat,
    ]);
    for (const op of ops.fnArray) if (paint.has(op)) return true;
    return false;
  } catch {
    return false;
  }
}

function describePages(
  provenance: ExtractedSource['provenance'],
  covered: number,
  total: number,
  runs: { firstSheet: number; lastSheet: number; offset: number; kind: string }[],
  conflicts: number,
): string {
  if (provenance === 'sheet') {
    return 'No printed page numbers were found, so page references are to the PDF sheet, and no pinpoint is checked against this file.';
  }
  // Showing the recovered series lets the reader sanity-check it at a glance —
  // which matters when the file is one chapter exported from a larger work and
  // the citation refers to the parent volume's pagination.
  const series = runs
    .slice(0, 3)
    .map((r) => `sheets ${r.firstSheet}–${r.lastSheet} = printed ${r.firstSheet + r.offset}–${r.lastSheet + r.offset}`)
    .join('; ');
  const where = series ? ` (${series})` : '';
  const base = `Printed page numbers recovered for ${covered} of ${total} sheets${where}.`;
  if (provenance === 'folio-corroborated') return `${base} Read off the page, and the file's own page labels agree.`;
  if (provenance === 'labels') return `${base} Taken from the file's own page labels.`;
  if (provenance === 'folio-contested') {
    return `${base} The file's own page labels DISAGREE with the printed folio on ${conflicts} sheets, so pinpoint references against this file are reported but never called wrong.`;
  }
  return `${base} Read off the page.`;
}
